import "./NewDiscountModal.css";

import { useSelector, useDispatch } from "react-redux";

import Modal from "@mui/material/Modal";
import { setDiscountModalState } from "../../../stateManagement/actions/claims";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ReactComponent as MagicIcon } from "../../../assets/image/svg/magicIcon.svg";
import { ReactComponent as PercentIcon } from "../../../assets/image/svg/percentageIcon.svg";
import DummyProductPngUrl  from "../../../assets/image/png/dummyProduct.png"
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import { useEffect, useRef } from "react";
import { getConfigData } from "../../../services/configurationService";
import { Scheme } from "../../createSecondaryOrder/models";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";


interface DiscountModalProps {
  skuData?: any;
  selectedSkuCode?: string;
  showFooter?: boolean;
  qtyLabel?: string;
  addRemoveQty?: boolean;
  showMrpAndPackSize?: boolean;
  selectedSecondaryScheme?: any;
  isClaimInvoiceView?: boolean;
  slabTransformation ?: boolean;
  isEditing?: boolean;
  insideManageInvoice?: boolean;
}

interface GroupedScheme {
  schemes: any[];
  priority: number;
  qpsFlag: boolean;
  schemeDescription: string;
  promoAttribute: string;
}

const slabUnitMap: Record<string, string> = {
  "cs_qps": "Cs",
  "cs": "Cs",
  "pc_qps": "Pc",
  "amount_qps": "amount",
  "amount": "amount",
  "Pc": "Pc",
  "pc": "Pc"
}

const NewDiscountModal: React.FC<DiscountModalProps> = ({
  skuData = {},
  selectedSkuCode = "",
  showFooter = true,
  qtyLabel,
  showMrpAndPackSize = false,
  selectedSecondaryScheme= {},
  isClaimInvoiceView = false,
  slabTransformation = true,
  isEditing,
  insideManageInvoice=false
}) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    mrpLabel,
    caseUnitLabel,
    otherUnitLabel,
    packUnitLabel,
    displayOtherUnitName,
    currency,
    designationBasedUomSecondary,
    batchIdConfiguration = false,
    manageSecondaryOrderV2 = false
  } = ConfigDictionary;

  const schemeNewView = useRef<boolean>(false)

  const fetchRequiredConfigs = async () => {
      try {
        //fetch config to display schemes New View in New Discount Modal
        const schemeNewViewConfig = await getConfigData("catalogue_configuration", "schemeNewViewDiscountModal")
        schemeNewView.current = schemeNewViewConfig[0]?.configValue ?? false;
      } catch (e) {
        console.error("Error while fetching configs", e);
      }
    };
    
  useEffect(() => {
    fetchRequiredConfigs()
  }, []);

  function getSlabOrBenefitValue(quantity: any, unit: string){
    //If unit is amount / value then currency symbol is used which is placed before the quantity
    if (unit === "amount" || unit === "value"){
      return `${currency} ${quantity}`
    }
    //If unit is other than currency, like Cs, Pc etc then place it after the quantity
    return `${quantity} ${unit}`
  }

  let selectedSku;

  if (batchIdConfiguration && isEditing && insideManageInvoice) {
    if (manageSecondaryOrderV2) {
      selectedSku = skuData?.orderDetails?.find(
        (obj: any) => obj?.batchId === selectedSkuCode && (obj?.type?.toLowerCase() !== 'freeitem')
      ) ?? {};
    } else {
      selectedSku = skuData?.orderDetails?.filter(
        (obj: any) => obj?.batchCode === selectedSkuCode && (obj?.type?.toLowerCase() !== 'freeitem')
      ) ?? [];
      const aggregated = selectedSku.reduce(
        (acc:any, obj:any) => { 
          acc.caseQuantity += obj.caseQuantity ?? 0;
          acc.pieceQuantity += obj.pieceQuantity ?? 0;
          acc.otherUnitQuantity += obj.otherUnitQuantity ?? 0;
          acc.discountValue += obj.discountValue ?? 0;
          if (Array.isArray(obj.discountInfo)) {
            acc.discountInfo.push(...obj.discountInfo);
          }
          return acc;
        },
        {
          caseQuantity: 0,
          pieceQuantity: 0,
          otherUnitQuantity: 0,
          discountValue: 0,
          discountInfo: [] as any[]
        }
      );
      selectedSku = {
        ...selectedSku[0],
        ...aggregated
      };
    }
  } else if (batchIdConfiguration && !isEditing && insideManageInvoice) {
    selectedSku = skuData?.orderDetails?.find(
      (obj: any) => obj?.batchId === selectedSkuCode && (obj?.type?.toLowerCase() !== 'freeitem')
    ) ?? {};
  } else {
    selectedSku = skuData?.orderDetails?.find(
      (obj: any) => obj?.batchCode === selectedSkuCode && (obj?.type?.toLowerCase() !== 'freeitem')
    ) ?? {};
  }

  const { discountModalState, selectedInvoiceRow } = useSelector((state: any) => state.claims);

  const dispatch = useDispatch();

  const handleOnClose = () => {
    dispatch(setDiscountModalState(false));
  };
  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];
  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);
  
  let processedDiscountInfo: any = [];
  const groupedSchemeMap: Record<string, GroupedScheme> = {};
  if (schemeNewView.current || isClaimInvoiceView) {
    if(!slabTransformation){
      //Discount Info from Promo service API needs no transformation
      processedDiscountInfo = isClaimInvoiceView ? selectedInvoiceRow?.discountInfo : selectedSku?.discountInfo;
    } 
    else if (isClaimInvoiceView ? selectedInvoiceRow?.discountInfo : selectedSku?.discountInfo) {
      processedDiscountInfo = (isClaimInvoiceView ? selectedInvoiceRow?.discountInfo : selectedSku?.discountInfo).map(
        (info: any) => ({
          discountId: info?.promoId,
          schemeDescription: info?.promoDescription || "",
          startQtyOrVal: info?.startRange ?? 0,
          endQtyOrVal: info?.endRange ?? 0,
          benefit: info?.benefitPercentage ?? info?.finalBenefit ?? info?.benefit ?? 0,
          promoId: info?.promoId || "",
          slabUnit: slabUnitMap[info?.rangeUnit] || "",
          benefitUnit: info?.promoType === "value" ? "value" : "%",
          qpsFlag: info?.promoAttribute?.some(
            (item: string) => {
              return (item.toLowerCase() === "qpsaggregationpromo" || item.toLowerCase() === "qpscalculationpromo")
            }
          ),
          priority: info?.priority ?? 0,
          promoAttribute: info?.promoAttribute?.[0] || "",
        })
      );
    } else {
      processedDiscountInfo = [];
    }

    for (const entry of processedDiscountInfo ?? []) {
      const { promoId, priority, qpsFlag, schemeDescription, promoAttribute } = entry;
      if (!groupedSchemeMap[promoId]) {
        groupedSchemeMap[promoId] = {
          schemes: [],
          priority,
          qpsFlag,
          schemeDescription,
          promoAttribute,
        };
      }
      groupedSchemeMap[promoId].schemes.push(entry);
    }
  }
  //Sort the schemes on the basis of priority
  const sortedGroupedScheme = (schemeNewView.current || isClaimInvoiceView) && Object.keys(groupedSchemeMap).length > 0
      ? Object.entries(groupedSchemeMap).sort(([, a], [, b]) => a.priority - b.priority)
      : [];

  if (designationEnabled && designation && designationKeys && matchedRole) {
    return (
      <div>
        <Modal
          open={discountModalState}
          onClose={handleOnClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <div className="right-aligned-modal">
            <div className="invoice-details">
              {isClaimInvoiceView && (
                <div>
                  <div className="claim-invoice">
                    <span className="invoice-no">
                      Invoice No. {selectedInvoiceRow?.invoiceNumber || selectedSecondaryScheme?.returnOrderNo}
                    </span>
                    <span className="invoice-date">{selectedInvoiceRow?.invoiceDate}</span>
                  </div>
                </div>
              )}
              <div className="product-details">
                <div className="product-details-card">
                  <img
                    alt="product-img"
                    src={
                      isClaimInvoiceView
                        ? selectedInvoiceRow?.blobKey || ConfigDictionary?.defaultCatalogueImage || DummyProductPngUrl
                        : selectedSku?.extendedAttributes?.blobKey ||ConfigDictionary?.defaultCatalogueImage|| DummyProductPngUrl
                    }
                    className="product-image"
                    style={{ padding: !selectedSku?.extendedAttributes?.blobKey ? "15px" : "0" }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = DummyProductPngUrl;
                    }}
                  />
                  {matchedRole.uomAllowed.piece?.trim() && (
                    <div className="product-info">
                      <h2>
                        {isClaimInvoiceView
                          ? selectedSecondaryScheme?.skuName ?? "-"
                          : selectedSku?.productInfo?.skuDescription ?? "-"}
                      </h2>
                      <p className="sku_code">
                        {" "}
                        {isClaimInvoiceView ? selectedSecondaryScheme?.skuCode ?? "-" : selectedSku?.batchCode ?? "-"}
                      </p>
                      {showMrpAndPackSize && (
                        <p>
                          {mrpLabel}
                          <span style={{ color: "#FF0000" }}>*</span> {currency} {selectedSku?.mrp ?? "-"}
                        </p>
                      )}
                    </div>
                  )}
                  {matchedRole.uomAllowed.case?.trim() && (
                    <div className="product-info">
                      <h2>
                        {isClaimInvoiceView
                          ? selectedSecondaryScheme?.skuName ?? "-"
                          : selectedSku?.productInfo?.skuDescription ?? "-"}
                      </h2>
                      <p className="sku_code">
                        {" "}
                        {isClaimInvoiceView ? selectedSecondaryScheme?.skuCode ?? "-" : selectedSku?.batchCode ?? "-"}
                      </p>
                      {showMrpAndPackSize && (
                        <p>
                          {mrpLabel}
                          <span style={{ color: "#FF0000" }}>*</span> {currency} {selectedSku?.caseMrp ?? "-"}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="close-modal-button" onClick={handleOnClose}>
                  <ChevronRightIcon style={{ color: "#fff", width: "30px", height: "30px" }} />
                </div>
              </div>

              <div className="totalQty-csPc" style={{ padding: "0 10px" }}>
                <div className="totalQty-csPc-container" style={{ marginBottom: "10px" }}>
                  <span>{qtyLabel ? qtyLabel : "Total Qty"}</span>
                  {matchedRole.uomAllowed.case?.trim() && caseUnitLabel && (
                    <div>
                      <span>
                        {`${
                          isClaimInvoiceView
                            ? selectedInvoiceRow?.cases ?? selectedSecondaryScheme?.damageCase ?? "0"
                            : selectedSku?.caseQuantity ?? "0"
                        } ${caseUnitLabel?.toUpperCase()}`}
                      </span>
                    </div>
                  )}
                  {matchedRole.uomAllowed.piece?.trim() && packUnitLabel && (
                    <div>
                      <span>
                        {`${
                          isClaimInvoiceView
                            ? selectedInvoiceRow?.pieces ?? selectedSecondaryScheme?.damagePcs ?? "0"
                            : selectedSku?.pieceQuantity ?? "0"
                        } ${packUnitLabel?.toUpperCase()}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="discounts-promotions">
                <div className="discountsAndPromoHeading">
                  <div className="discountsAndPromoHeadingLabel">
                    <MagicIcon />
                    <p>Discounts & Promotions</p>
                  </div>
                </div>
                {
                  (selectedInvoiceRow?.discountsAndPromotions?.length ?? 0) === 0 &&
                  (selectedSku?.discountInfo?.length ?? 0) === 0 ?
                  <SimpleBar style={{height: "100%", overflow: "auto", marginBottom: "10px", scrollbarWidth: "none"}}>
                  <div className="discounts-list-container" style={{ alignItems: "center", justifyContent: "center" }}>
                    <RenderSvg name={"noPromoAvailableIcon"}/>
                    <span className="no-promotion-available-label">No Promotions Available</span>
                  </div>
                  </SimpleBar>
                  :
                  <SimpleBar style={{height: "100%", overflow: "auto", marginBottom: "10px", scrollbarWidth: "none"}}>
                  <div className="discounts-list-container">
                  {isClaimInvoiceView ? (
                    schemeNewView.current ? (
                      sortedGroupedScheme.map(([promoId, groupedEntry]) => (
                        <div className="new-discount-container" key={promoId}>
                          <div style={{ margin: "5px" }}>
                            <div className="new-discount-details-heading">
                              {groupedEntry.qpsFlag && (
                                <div style={{ height: "12px", marginTop: "1px" }}>
                                  <RenderSvg name="qpsIcon" />
                                </div>
                              )}
                              <div style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                {groupedEntry.promoAttribute ? `${groupedEntry.schemeDescription} (${groupedEntry.promoAttribute})` : groupedEntry.schemeDescription}
                              </div>
                            </div>
                            <ul>
                              {groupedEntry.schemes.map((scheme, index) => (
                                <li key={index} className="new-discount-details">
                                  Buy from{" "}
                                  {getSlabOrBenefitValue(
                                    scheme?.startQtyOrVal,
                                    slabUnitMap[scheme?.slabUnit.toLowerCase()]
                                  )}{" "}
                                  to{" "}
                                  {getSlabOrBenefitValue(
                                    scheme?.endQtyOrVal,
                                    slabUnitMap[scheme?.slabUnit.toLowerCase()]
                                  )}{" "}
                                  & get {getSlabOrBenefitValue(scheme?.benefit, scheme?.benefitUnit)} off
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))
                    ) : (
                      selectedInvoiceRow?.discountsAndPromotions?.map((discount: string, index: number) => (
                        <div className="discount-container" key={index}>
                          <div className="discount-container-left">
                            <div style={{ display: "flex" }}>
                              <PercentIcon width={17} height={17} />
                            </div>
                            <div className="discount-details">
                              <p>{discount}</p>
                            </div>
                          </div>
                          <span className="dropDownIcon"></span>
                        </div>
                      ))
                    )
                  ) : schemeNewView.current ? (
                    sortedGroupedScheme.map(([promoId, groupedEntry]) => (
                      <div className="new-discount-container" key={promoId}>
                        <div style={{ margin: "5px" }}>
                          <div className="new-discount-details-heading">
                            {groupedEntry.qpsFlag && (
                              <div style={{ height: "12px", marginTop: "1px" }}>
                                <RenderSvg name="qpsIcon" />
                              </div>
                            )}
                            <div style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                              {groupedEntry.promoAttribute ? `${groupedEntry.schemeDescription} (${groupedEntry.promoAttribute})` : groupedEntry.schemeDescription}
                            </div>
                          </div>
                          <ul>
                            {groupedEntry.schemes.map((scheme, index) => (
                              <li key={index} className="new-discount-details">
                                Buy from{" "}
                                {getSlabOrBenefitValue(
                                  scheme?.startQtyOrVal,
                                  slabUnitMap[scheme?.slabUnit.toLowerCase()]
                                )}{" "}
                                to{" "}
                                {getSlabOrBenefitValue(
                                  scheme?.endQtyOrVal,
                                  slabUnitMap[scheme?.slabUnit.toLowerCase()]
                                )}{" "}
                                & get {getSlabOrBenefitValue(scheme?.benefit, scheme?.benefitUnit)} off
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))
                  ) : (
                    selectedSku?.discountInfo?.map((discount: any) => {
                      if (discount?.id === "focDiscountId") {
                        return <></>;
                      }
                      return (
                        <div className="discount-container">
                          <div className="discount-container-left">
                            <div style={{ display: "flex" }}>
                              <PercentIcon width={17} height={17} />
                            </div>
                            <div className="discount-details">
                              <p>{discount?.discountInfo ?? discount?.promoDescription ?? "-"}</p>
                            </div>
                          </div>
                          <span className="dropDownIcon"></span>
                        </div>
                      );
                    })
                  )}
                  </div>
                  </SimpleBar>
                }
                
              </div>

              {showFooter && (
                <div className="total-discount-value">
                  <span>Total Discount Value :</span>
                  <span>
                    {currency}{" "}
                    {isClaimInvoiceView
                      ? typeof selectedInvoiceRow?.finalAmount === "number"
                        ? formatDecimal(selectedInvoiceRow.finalAmount)
                        : selectedSecondaryScheme?.claimAmount?.replace(currency, "") ?? "0.00"
                      : ((selectedSku?.discountValue ?? 0) + (selectedSku?.couponValue ?? 0)).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <div>
      <Modal
        open={discountModalState}
        onClose={handleOnClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="right-aligned-modal">
          <div className="invoice-details">
            <div>
              {isClaimInvoiceView ? (
                <>
                  <div className="claim-invoice">
                    <span className="invoice-no">
                      Invoice No. {selectedInvoiceRow?.invoiceNumber || selectedSecondaryScheme?.returnOrderNo}
                    </span>
                    <span className="invoice-date">{selectedInvoiceRow?.invoiceDate}</span>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
            <div className="product-details">
              <div className="product-details-card">
                <img
                  alt="product-img"
                  src={selectedSku?.extendedAttributes?.blobKey || selectedInvoiceRow?.blobKey ||ConfigDictionary?.defaultCatalogueImage || DummyProductPngUrl}
                  className="product-image"
                  style={{ padding: !selectedSku?.extendedAttributes?.blobKey ? "15px" : "0" }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = DummyProductPngUrl;
                  }}
                />
                <div className="product-info">
                  <h2>
                    {isClaimInvoiceView
                      ? selectedSecondaryScheme?.skuName ?? "-"
                      : selectedSku?.productInfo?.skuDescription ?? "-"}
                  </h2>
                  <p className="sku_code">
                    {isClaimInvoiceView ? selectedSecondaryScheme?.skuCode ?? "-" : selectedSku?.batchCode ?? "-"}
                  </p>
                  {showMrpAndPackSize && (
                    <p>
                      {mrpLabel}
                      <span style={{ color: "#FF0000" }}>*</span> {currency} {selectedSku?.mrp ?? "-"}
                    </p>
                  )}
                </div>
              </div>

              <div className="close-modal-button" onClick={handleOnClose}>
                <ChevronRightIcon style={{ color: "#fff", width: "30px", height: "30px" }} />
              </div>
            </div>
            <div className="totalQty-csPc" style={{ padding: "0 10px" }}>
              <div className="totalQty-csPc-container" style={{ marginBottom: "10px" }}>
                <span>{qtyLabel ? qtyLabel : "Total Qty"}</span>
                <div>
                  <span>
                    {`${
                      isClaimInvoiceView
                        ? selectedInvoiceRow?.cases ?? selectedSecondaryScheme?.damageCase ?? "0"
                        : selectedSku?.caseQuantity ?? "0"
                    } ${caseUnitLabel?.toUpperCase()}`}
                  </span>
                </div>
                {(displayOtherUnitName || otherUnitLabel) && (
                  <div>
                    <span>
                      {`${
                        isClaimInvoiceView
                          ? selectedInvoiceRow?.others ?? selectedSecondaryScheme?.damageOthers ?? "0"
                          : selectedSku?.otherUnitQuantity ?? "0"
                      } ${isClaimInvoiceView ? selectedInvoiceRow?.otherUnitName || otherUnitLabel?.toUpperCase()?.slice(0, 3) : otherUnitLabel?.toUpperCase()?.slice(0, 3)}`}
                    </span>
                  </div>
                )}
                {(packUnitLabel) && (
                  <div>
                    <span>
                      {`${
                        isClaimInvoiceView
                          ? selectedInvoiceRow?.pieces ?? selectedSecondaryScheme?.damagePcs ?? "0"
                          : selectedSku?.pieceQuantity ?? "0"
                      } ${packUnitLabel?.toUpperCase()}`}
                    </span>
                  </div>
                )
              }
              </div>
            </div>
            <div className="discounts-promotions">
              <div className="discountsAndPromoHeading">
                <div className="discountsAndPromoHeadingLabel">
                  <MagicIcon />
                  <p>Discounts & Promotions</p>
                </div>
                <div className=""></div>
              </div>
              {
                (selectedInvoiceRow?.discountsAndPromotions?.length ?? 0) === 0 &&
                (selectedSku?.discountInfo?.length ?? 0) === 0 ?
                <SimpleBar style={{height: "100%", overflow: "auto", marginBottom: "10px", scrollbarWidth: "none"}}>
                <div className="discounts-list-container" style={{ alignItems: "center", justifyContent: "center" }}>
                  <RenderSvg name={"noPromoAvailableIcon"}/>
                  <span className="no-promotion-available-label">No Promotions Available</span>
                </div>
                </SimpleBar>
                :
                <SimpleBar style={{height: "100%", overflow: "auto", marginBottom: "10px", scrollbarWidth: "none"}}>
                <div className="discounts-list-container">
                {isClaimInvoiceView ? (
                  schemeNewView.current ? (
                    sortedGroupedScheme.map(([promoId, groupedEntry]) => (
                      <div className="new-discount-container" key={promoId}>
                        <div style={{ margin: "5px" }}>
                          <div className="new-discount-details-heading">
                            {groupedEntry.qpsFlag && (
                              <div style={{ height: "12px", marginTop: "1px" }}>
                                <RenderSvg name="qpsIcon" />
                              </div>
                            )}
                            <div style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                              {groupedEntry.promoAttribute ? `${groupedEntry.schemeDescription} (${groupedEntry.promoAttribute})` : groupedEntry.schemeDescription}
                            </div>
                          </div>
                          <ul>
                            {groupedEntry.schemes.map((scheme, index) => (
                              <li key={index} className="new-discount-details">
                                Buy from{" "}
                                {getSlabOrBenefitValue(
                                  scheme?.startQtyOrVal,
                                  slabUnitMap[scheme?.slabUnit.toLowerCase()]
                                )}{" "}
                                to{" "}
                                {getSlabOrBenefitValue(
                                  scheme?.endQtyOrVal,
                                  slabUnitMap[scheme?.slabUnit.toLowerCase()]
                                )}{" "}
                                & get {getSlabOrBenefitValue(scheme?.benefit, scheme?.benefitUnit)} off
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))
                  ) : (
                    selectedInvoiceRow?.discountsAndPromotions?.map((discount: string, index: number) => (
                      <div className="discount-container" key={index}>
                        <div className="discount-container-left">
                          <div style={{ display: "flex" }}>
                            <PercentIcon width={17} height={17} />
                          </div>
                          <div className="discount-details">
                            <p>{discount}</p>
                          </div>
                        </div>
                        <span className="dropDownIcon"></span>
                      </div>
                    ))
                  )
                ) : schemeNewView.current ? (
                  sortedGroupedScheme.map(([promoId, groupedEntry]) => (
                    <div className="new-discount-container" key={promoId}>
                      <div style={{ margin: "5px" }}>
                        <div className="new-discount-details-heading">
                          {groupedEntry.qpsFlag && (
                            <div style={{ height: "12px", marginTop: "1px" }}>
                              <RenderSvg name="qpsIcon" />
                            </div>
                          )}
                          <div style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            {groupedEntry.promoAttribute ? `${groupedEntry.schemeDescription} (${groupedEntry.promoAttribute})` : groupedEntry.schemeDescription}
                          </div>
                        </div>
                        <ul>
                          {groupedEntry.schemes.map((scheme, index) => (
                            <li key={index} className="new-discount-details">
                              Buy from{" "}
                              {getSlabOrBenefitValue(
                                scheme?.startQtyOrVal,
                                slabUnitMap[scheme?.slabUnit.toLowerCase()]
                              )}{" "}
                              to{" "}
                              {getSlabOrBenefitValue(
                                scheme?.endQtyOrVal,
                                slabUnitMap[scheme?.slabUnit.toLowerCase()]
                              )}{" "}
                              & get {getSlabOrBenefitValue(scheme?.benefit, scheme?.benefitUnit)} off
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))
                ) : (
                  selectedSku?.discountInfo?.map((discount: any) => {
                    if (discount?.id === "focDiscountId") {
                      return <></>;
                    }
                    return (
                      <div className="discount-container">
                        <div className="discount-container-left">
                          <div style={{ display: "flex" }}>
                            <PercentIcon width={17} height={17} />
                          </div>
                          <div className="discount-details">
                            <p>{discount?.discountInfo ?? discount?.promoDescription ?? "-"}</p>
                          </div>
                        </div>
                        <span className="dropDownIcon"></span>
                      </div>
                    );
                  })
                )}
                </div>
                </SimpleBar>
              }

            </div>
            {showFooter ? (
              <div className="total-discount-value-secondary">
                <span>Total Discount Value :</span>
                <span>
                  {currency}{" "}
                  {isClaimInvoiceView
                    ? typeof selectedInvoiceRow?.finalAmount === "number"
                      ? formatDecimal(selectedInvoiceRow.finalAmount)
                      : selectedSecondaryScheme?.claimAmount?.replace(currency, "") ?? "0.00"
                    : ((selectedSku?.discountValue ?? 0) + (selectedSku?.couponValue ?? 0)).toFixed(2)}
                </span>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NewDiscountModal;
